import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Shared state for the Rate Quoting Skill — drives per-row execution, the
 * workflow timeline, and the automation settings shown in the right-side
 * workspace panel.
 *
 * Execution path: triggerRun() first tries the live MyAMP backend
 * (POST /api/ams/rate-quoting/runs + SSE stream). If the backend is
 * unreachable (not deployed yet, network error, missing tokens) it falls back
 * to a local simulation so the UI demo keeps working.
 */
const RATE_QUOTING_BASE = `${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting`;
const PROSPECTS_BASE = `${(process.env.REACT_APP_Auth_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/AMPNowcerts`;

/** Normalize the backend run result_payload ({quotes:[...]}) into QuoteResult[]. */
function mapQuoteResults(result: any): QuoteResult[] {
  const raw = Array.isArray(result?.quotes) ? result.quotes : [];
  return raw.map((q: any) => ({
    quoteId:         q.quoteId,
    quoteDatabaseId: q.quoteDataBaseId ?? q.quoteDatabaseId ?? null,
    carrierId:       q.carrierId,
    carrierName:     q.carrierName || q.carrierId,
    carrierLogoURL:  q.carrierLogoURL,
    premiumAmount:   q.premiumAmount ?? null,
    totalAmount:     q.totalAmount ?? null,
    quoteType:       q.quoteType,
    statusCode:      q?.quoteStatus?.statusCode ?? q.quoteType,
    carrierBridgingLink: q.carrierBridgingLink ?? null,
    failureReason: Array.isArray(q.failures) && q.failures.length ? q.failures[0]?.reason : undefined,
    coverages:     Array.isArray(q.coverages) ? q.coverages : null,
    contractTerm:  q.contractTerm ?? null,
  }));
}

/** Pretty-print a `YYYY-MM-DD` (or any Date-parseable string) for the table.
 *  Falls back to the raw value if parsing fails so we never render "Invalid". */
function formatDisplayDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "$1,234" — matches the existing dummy-row formatting. */
function formatPremiumCurrency(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/** Map a row from GET /rate-quoting/renewals → QuoteRow for the table. The
 *  backend already returns rate-quoting-friendly snake_case; this is mostly
 *  field renaming + date / currency formatting. */
function mapRenewalRow(r: any): QuoteRow {
  const status: QuoteRowStatus =
    r?.status === 'in-progress' || r?.status === 'completed' || r?.status === 'failed'
      ? r.status
      : 'pending';
  return {
    id:             String(r?.id ?? ''),
    date:           formatDisplayDate(r?.expiration_date),
    insured:        r?.insured || '—',
    carrier:        r?.carrier || r?.line_of_business || '—',
    premium:        formatPremiumCurrency(r?.premium),
    status,
    lineOfBusiness: r?.line_of_business || undefined,
    effectiveDate:  formatDisplayDate(r?.effective_date),
    expirationDate: formatDisplayDate(r?.expiration_date),
    expirationDateRaw: r?.expiration_date || undefined,
    policyNumber:   r?.policy_number || undefined,
    insuredId:      r?.insured_id   || undefined,
    producer:       r?.producer || undefined,
  };
}

export type RunStatus  = 'running' | 'awaiting_input' | 'completed' | 'failed';
export type RunTrigger = 'ai' | 'manual';
export type StepStatus = 'pending' | 'running' | 'awaiting_input' | 'completed' | 'failed';

/** One sub-step the backend emits during a step's execution. Drives the
 *  "thinking" trail rendered beneath each step row. */
export interface ProgressItem {
  key: string;
  label: string;
  status: 'running' | 'completed' | 'failed';
  ts?: string;
  detail?: string;
}

/** One field requested by an awaiting-input prompt. */
export interface AwaitingInputField {
  /** Backend sends `key` (preferred). Legacy paths may send `name`. */
  key?: string;
  name?: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select';
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

/** Read-only confirmation summary surfaced before the final carrier
 *  submission, when the user chose "Execute with review". */
export interface AwaitingReviewPayload {
  insured_name?: string;
  state?: string;
  effective_date?: string;
  policy_type?: string;
  application_id?: string;
  drivers_count?: number;
  vehicles_count?: number;
  drivers?:  Array<{
    driver_id?: string | number | null;
    name?: string;
    dob?: string;
    /** True when the DOB value fails the rater's age validation (< 14 or future date).
     *  The producer should correct it via chat before submitting to carriers. */
    dob_invalid?: boolean;
    license_number?: string;
    license_state?: string;
  }>;
  vehicles?: Array<{
    vehicle_id?: string | number | null;
    year?: string | number;
    make?: string;
    model?: string;
    vin?: string;
    usage?: string;
  }>;
  carriers?: Array<{ name?: string; provider_id?: string | number }>;
  /** Flat list of key/value coverage facts from the rater's policy_defaults. */
  coverages?: Array<{ key: string; value: string }>;
  /** List of data issues detected before submission — surfaced to the review chat
   *  so the AI can proactively ask the producer to correct them. */
  data_warnings?: Array<{
    field: string;
    message: string;
    severity: 'error' | 'warning';
    context: 'driver' | 'vehicle' | 'policy';
    driver_id?: string | number | null;
    driver_name?: string;
    vehicle_id?: string | number | null;
  }>;
}

export interface AwaitingInputPrompt {
  prompt: string;
  /** Optional WHY-line shown beneath the prompt as a chat bubble. */
  reason?: string;
  /** When empty / missing, the UI renders a single freeform reply textarea
   *  (chat-style). When non-empty, the UI renders the structured form. */
  fields: AwaitingInputField[];
  /** When present, the UI renders a read-only review card with a single
   *  Submit/Cancel pair (instead of any input form). */
  review?: AwaitingReviewPayload;
}

/* ── Row type shared between page table & sidebar Details view ─────────── */
export type QuoteRowStatus = 'pending' | 'in-progress' | 'completed' | 'failed';

export interface QuoteRow {
  id: string;
  date: string;
  insured: string;
  carrier: string;
  premium: string;
  status: QuoteRowStatus;
  lineOfBusiness?: string;
  effectiveDate?: string;
  expirationDate?: string;
  expirationDateRaw?: string;
  policyNumber?: string;
  insuredId?: string;
  phone?: string;
  producer?: string;
  notes?: string;
  /** Raw prospect data from NowCerts — passed to voice agent as dynamic variables */
  rawProspect?: Record<string, any>;
}

/** One opportunity record from NowCerts OpportunitiesList OData. */
export interface OpportunityRecord {
  databaseId: string;
  lineOfBusinessName: string | null;
  neededBy: string | null;
  opportunityStageName: string | null;
  winProbability: string | null;
  agencyCommission: number | null;
  assignedTo: string[];
  description: string | null;
  policyNumbers: string[];
  createdFromRenewal: boolean;
  createDate: string;
  changeDate: string;
  insuredDatabaseId: string;
}

export interface WorkflowStep {
  id: string;
  label: string;
  description?: string;
  status: StepStatus;
  startedAt?: Date;
  completedAt?: Date;
  durationMs?: number;
  /** Error message set when status === 'failed'. */
  error?: string | null;
  /** Live sub-step trail emitted by the backend during this step. */
  progress?: ProgressItem[];
  /** If the step is paused asking for user input, this is the prompt + fields. */
  awaitingInput?: AwaitingInputPrompt | null;
}

export interface RunEntry {
  id: string;
  status: RunStatus;
  startedAt: Date;
  completedAt?: Date;
  durationMs?: number;
  label: string;
  trigger: RunTrigger;
  executedBy: string;
  triggerSource: string;
  automationType: string;
  /** Row this run was triggered for (null for legacy / batch). */
  rowId?: string | null;
  /** Workflow lane: 'new' or 'renewal'. */
  workflow?: 'new' | 'renewal';
  /** Policy type from result_payload (PAP / HO). */
  policyType?: string;
  /** Effective date from result_payload. */
  effectiveDate?: string;
  /** Step-by-step execution timeline. */
  steps: WorkflowStep[];
  /** Carrier quote results returned by the final step (premium / failures). */
  quotes?: QuoteResult[];
  /** Recommendation computed after all quotes are collected. */
  recommendation?: {
    best_quote_id?: string | null;
    ranking?: Array<{
      quote_id?:       string;
      carrier?:        string;
      annual_premium?: number;
      score?:          number;
      reason?:         string;
    }>;
  } | null;
  /** Auto-generated proposal for the best quote (streamed after run completes). */
  autoProposal?: {
    carrier?:        string;
    quote_id?:       string;
    quote_db_id?:    string;
    proposal_url?:   string | null;
    premium_annual?: number | null;
    reason?:         string;
    error?:          string;
  } | null;
  /** Live step label from scope:'auto_proposal_step' SSE frames — drives ProposalGeneratingCard text. */
  autoProposalStep?: { step: string; label: string } | null;
  /** For renewal runs: the current active policy's annual premium fetched during r1.
   *  Null for new-business runs or when the policy has no recorded premium. */
  currentPremium?: number | null;
  /** Quote Summary PDF generated after quoting completes. */
  quoteSummary?: {
    file_url:      string;
    file_name:     string;
    best_carrier:  string;
    best_premium:  number;
    carrier_count: number;
    policy_type:   string;
    insured_name?: string;
  } | null;
}

/** One event from the rate_quote_events table (SSE scope:"event" or GET /history). */
export interface EventLogEntry {
  id:         number;
  run_id:     string;
  actor:      string;
  event_type: string;
  row_id?:    string | null;
  payload?:   any;
  created_at: string;
}

/** One coverage line inside a carrier quote. */
export interface QuoteCoverage {
  coverageCode?: string;
  coverageName?: string;
  limit?: string;
  deductible?: string;
  premium?: number | null;
}

/** One carrier quote returned by get_personal_lines_quote_results. */
export interface QuoteResult {
  quoteId?: string;
  /** NowCerts quote GUID returned after AMS persistence — use this as
   *  quote_database_id when calling the proposal generation endpoint. */
  quoteDatabaseId?: string | null;
  carrierId?: string;
  carrierName?: string;
  carrierLogoURL?: string;
  premiumAmount?: number | null;
  totalAmount?: number | null;
  quoteType?: string;                 // 'QUOTED' | 'FAILED' | ...
  statusCode?: string;                // from quoteStatus.statusCode
  carrierBridgingLink?: string | null;
  failureReason?: string;
  coverages?: QuoteCoverage[] | null;
  contractTerm?: string | null;
}

/** Slim snapshot of one historical quote run, passed to the right-side sidebar
 *  when the producer clicks a "Quote #N" block in the row's expanded history. */
export interface SelectedQuoteRunData {
  runNumber?: number;
  executedAt: string;
  runStatus: 'completed' | 'failed' | 'running' | 'awaiting_input';
  durationMs?: number | null;
  carriers: Array<{
    name: string;
    premium: string;
    premiumRaw: number | null;
    termLabel: string;
    monthlyPremium: string;
    status: 'quoted' | 'declined' | 'failed';
    failureReason?: string | null;
    logoURL?: string | null;
    bridgingLink?: string | null;
    amBestRating?: string;
  }>;
  rawCoverages?: Array<{ key: string; value: string }>;
  submittedDriversCount?: number | null;
  submittedVehiclesCount?: number | null;
  errorMessage?: string | null;
  /** Renewal-specific: the current policy premium before re-quoting */
  isRenewal?: boolean;
  currentPremium?: number | null;
  currentCarrier?: string | null;
}

export interface RateQuoteLinkSettings {
  renewal: {
    enabled: boolean;             // master switch — only upsert routine when true
    daysBeforeExpiry: number;
    autoRunTime: string;          // "HH:MM" 24-hour
    autoSendProposal: boolean;
    timezone?: string;            // IANA timezone e.g. "America/New_York"
    tags?: string[];              // NowCerts tag names — only trigger for matching insureds
  };
  newLead: {
    enabled: boolean;             // master switch — only upsert routine when true
    aiAutoExecute: boolean;
    enrichData: boolean;
    autoSendProposal: boolean;
    tags?: string[];              // NowCerts tag names — only trigger for matching prospects
  };
  /** Momentum Voice — outbound calling for new leads. */
  voice?: {
    enabled: boolean;
    agentId: string | null;
    agentName: string | null;
    agentPhoneNumberId: string | null;
  };
  /** Routine IDs created by the automation setup — persisted to backend _meta. */
  _meta?: {
    newLeadRoutineId?: string | null;
    renewalRoutineId?: string | null;
    voiceRoutineId?: string | null;
  };
}

export type PanelView = 'details' | 'workflow' | 'history' | 'settings' | 'quote-detail' | 'voice-history';

interface RateQuoteLinkContextValue {
  history: RunEntry[];
  isRunning: boolean;
  activeRun?: RunEntry;
  lastRun?: RunEntry;
  settings: RateQuoteLinkSettings;
  settingsLoading: boolean;
  updateSettings: (next: RateQuoteLinkSettings) => void;
  /** Re-read settings from the API (and localStorage fallback).
   *  Call this when returning from a page that may have changed the
   *  Momentum Routine isActive flag (e.g. MomentumRoutinesPage toggle). */
  refreshSettings: () => void;
  triggerRun: (opts: {
    rowId: string;
    workflow: 'new' | 'renewal';
    label: string;
    insuredName?: string;
    insuredId?: string;
    /** When true, the backend pauses for a review/confirm step before sending
     *  to carriers. Default false → existing one-click submit. */
    withReview?: boolean;
    /** Policy type to quote. "PAP" = Personal Auto (default), "HO" = Home Owners, "BUNDLE" = Home + Auto. */
    policyType?: 'PAP' | 'HO' | 'BUNDLE';
    /** For renewals: use the active policy's expiration date as the new quote's effective date. YYYY-MM-DD format. */
    effectiveDate?: string;
  }) => string;  // returns the local run ID so the caller can open the panel immediately
  /** Resume a paused step by posting user-provided inputs. The backend
   *  workflow's awaiter poller picks them up and continues automatically. */
  submitStepInput: (runId: string, stepId: string, inputs: Record<string, any>) => Promise<void>;
  /** Resolve a local run-ID (e.g. "run-1234567890") to the backend UUID.
   *  Falls back to the local ID if no mapping exists (simulation mode). */
  getBackendId: (localId: string) => string;
  /** Re-attach the workflow panel to an existing backend run (e.g. from Quote
   *  History). Fetches the current snapshot, seeds the RunEntry into history,
   *  sets it as the active run, and re-subscribes the SSE stream. */
  reattachRun: (backendRunId: string) => Promise<void>;
  /** Which view the right-side workspace panel is showing. */
  panelView: PanelView;
  setPanelView: (v: PanelView) => void;
  /** Row currently selected in the page table, surfaced in the Details view. */
  selectedRow: QuoteRow | null;
  setSelectedRow: (row: QuoteRow | null) => void;
  /** Quote run selected from the row's history panel — opens the Quote Detail sidebar view. */
  selectedQuoteRun: SelectedQuoteRunData | null;
  setSelectedQuoteRun: (run: SelectedQuoteRunData | null) => void;
  /** Increments whenever something on the page wants the right-side workspace
   *  panel to expand (Settings click, Execute click, row click). The SidebarRight
   *  component listens for changes and calls its own onToggle if collapsed. */
  openSignal: number;
  requestOpen: () => void;
  /** Increments whenever the inline workflow panel opens — SidebarRight collapses. */
  closeSignal: number;
  requestClose: () => void;
  /** Real persisted runs fetched from the backend (History tab). Null until the
   *  first fetch resolves; falls back to the in-session `history` when null. */
  historyRuns: RunEntry[] | null;
  /** Real upcoming renewals fetched from the backend (Renewal tab). Null while
   *  the first fetch is in flight or has failed; in that case the page falls
   *  back to its placeholder rows. An empty array means "fetched, none found". */
  renewals: QuoteRow[] | null;
  /** The actual date window that was used for the last successful renewals fetch
   *  (start/end in YYYY-MM-DD). Used to pre-fill the date range inputs so the
   *  filter always reflects what data is currently displayed. */
  renewalWindow: { start: string; end: string } | null;
  /** Re-fetch renewals from the backend with an explicit date window. When
   *  `start`/`end` are omitted the backend falls back to today → today +
   *  `daysBeforeExpiry` (which itself defaults to 60). All params are
   *  forwarded to GET /AMPNowcerts/renewals as query string. */
  refreshRenewals: (opts?: {
    daysBeforeExpiry?: number;
    start?: string;   // YYYY-MM-DD
    end?: string;     // YYYY-MM-DD
    search?: string;  // free-text insured name filter (server-side)
    lobType?: string; // 'personal' (default) | 'commercial'
  }) => Promise<void>;
  /** New Business prospects from NowCerts (type == 1). Null = loading,
   *  [] = fetched + empty. Pagination metadata is also exposed. */
  prospects: QuoteRow[] | null;
  prospectsTotal: number;
  prospectsPage: number;
  prospectsTotalPages: number;
  /** Re-fetch prospects. Pass search + page to drive the table. */
  refreshProspects: (opts?: {
    search?: string;
    page?: number;
    pageSize?: number;
    fromDate?: string;
    toDate?: string;
  }) => Promise<void>;
  /** Opportunities for the currently selected prospect. Null = not yet fetched / loading. */
  opportunities: OpportunityRecord[] | null;
  opportunitiesTotal: number;
  /** Fetch opportunities for a given insured databaseId (prospect row click). */
  refreshOpportunities: (insuredDatabaseId: string) => Promise<void>;
  /** Clear the loaded opportunities (e.g. when selection changes). */
  clearOpportunities: () => void;
  /** Event log entries keyed by backend run UUID. Populated by SSE scope:"event" frames
   *  and hydrated from GET /runs/{id}/history on reconnect. */
  runEvents: Record<string, EventLogEntry[]>;
  /** Fetch (or refresh) the event log for a specific backend run. */
  refreshRunEvents: (backendRunId: string) => Promise<void>;
}

const RateQuoteLinkContext = createContext<RateQuoteLinkContextValue | null>(null);

/* ── Workflow step templates ──────────────────────────────────────────── */

const NEW_STEPS: Omit<WorkflowStep, 'status'>[] = [
  { id: 's1', label: 'Find Quote',            description: 'Match insured to carrier appetite' },
  { id: 's2', label: 'Prefill / Enrich Data', description: 'Pull from AMS & apply smart defaults' },
  { id: 's3', label: 'Generate Quote',        description: 'Build, submit & retrieve carrier premiums' },
  { id: 's4', label: 'Generate Proposal',     description: 'Build AI recommended proposal & PDF' },
];

const RENEWAL_STEPS: Omit<WorkflowStep, 'status'>[] = [
  { id: 'r1', label: 'Detect Renewal',        description: 'Identify policy nearing expiry' },
  { id: 'r2', label: 'Pull Current Policy',   description: 'Fetch coverage & apply smart defaults' },
  { id: 'r3', label: 'Re-quote with Carriers', description: 'Build, submit & retrieve premiums' },
  { id: 'r4', label: 'Generate Proposal',     description: 'Build AI recommended proposal & PDF' },
];

const BUNDLE_STEPS: Omit<WorkflowStep, 'status'>[] = [
  { id: 'b1', label: 'Find Insured',      description: 'Lookup insured for both legs' },
  { id: 'b2', label: 'Appetite Check',    description: 'Combined Home + Auto carrier check' },
  { id: 'b3', label: 'Home Leg',          description: 'Create & fill Home application' },
  { id: 'b4', label: 'Auto Leg',          description: 'Create & fill Auto application' },
  { id: 'b5', label: 'Generate Quotes',   description: 'Submit both legs & retrieve premiums' },
  { id: 'b6', label: 'Finalize',          description: 'Combined results & proposal' },
];

function buildSteps(workflow: 'new' | 'renewal', policyType?: string): WorkflowStep[] {
  const tmpl = policyType === 'BUNDLE' ? BUNDLE_STEPS
    : workflow === 'new' ? NEW_STEPS
    : RENEWAL_STEPS;
  return tmpl.map(s => ({ ...s, status: 'pending' as StepStatus }));
}

/* ── Defaults ─────────────────────────────────────────────────────────── */

const DEFAULT_VOICE_SETTINGS = {
  enabled:            false,
  agentId:            null as string | null,
  agentName:          null as string | null,
  agentPhoneNumberId: null as string | null,
};

const DEFAULT_SETTINGS: RateQuoteLinkSettings = {
  renewal: {
    enabled: false,
    daysBeforeExpiry: 60,
    autoRunTime: '08:00',
    autoSendProposal: true,
    tags: [],
  },
  newLead: {
    enabled: false,
    aiAutoExecute: true,
    enrichData: true,
    autoSendProposal: false,
    tags: [],
  },
  voice: { ...DEFAULT_VOICE_SETTINGS },
  _meta: {},
};

const SETTINGS_KEY = 'rateQuotingSkill.settings.v1';

function loadSettings(): RateQuoteLinkSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return {
      renewal: { ...DEFAULT_SETTINGS.renewal, ...(parsed.renewal ?? {}) },
      newLead: { ...DEFAULT_SETTINGS.newLead, ...(parsed.newLead ?? {}) },
      voice:   { ...DEFAULT_VOICE_SETTINGS,   ...(parsed.voice   ?? {}) },
      _meta:   { ...(DEFAULT_SETTINGS._meta ?? {}), ...(parsed._meta ?? {}) },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}


/* ── Provider ─────────────────────────────────────────────────────────── */

export const RateQuoteLinkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [history, setHistory] = useState<RunEntry[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [settings, setSettings] = useState<RateQuoteLinkSettings>(() => loadSettings());
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [panelView, setPanelViewRaw] = useState<PanelView>('details');
  // Wrap setPanelView: switching to 'details' clears the active run so stale
  // workflow state never bleeds into a different row's / tab's details view.
  const setPanelView = useCallback((v: PanelView) => {
    setPanelViewRaw(v);
    if (v === 'details') {
      setActiveRunId(null);
      setIsRunning(false);
    }
  }, []);
  const [selectedRow, setSelectedRow] = useState<QuoteRow | null>(null);
  const [openSignal, setOpenSignal] = useState(0);
  const requestOpen = useCallback(() => setOpenSignal(s => s + 1), []);
  const [closeSignal, setCloseSignal] = useState(0);
  const requestClose = useCallback(() => setCloseSignal(s => s + 1), []);
  const [historyRuns, setHistoryRuns] = useState<RunEntry[] | null>(null);
  const [renewals, setRenewals] = useState<QuoteRow[] | null>(null);
  const [renewalWindow, setRenewalWindow] = useState<{ start: string; end: string } | null>(null);
  const [prospects, setProspects] = useState<QuoteRow[] | null>(null);
  const [prospectsTotal, setProspectsTotal] = useState(0);
  const [prospectsPage, setProspectsPage] = useState(1);
  const [prospectsTotalPages, setProspectsTotalPages] = useState(1);
  const [opportunities, setOpportunities] = useState<OpportunityRecord[] | null>(null);
  const [opportunitiesTotal, setOpportunitiesTotal] = useState(0);
  const [selectedQuoteRun, setSelectedQuoteRun] = useState<SelectedQuoteRunData | null>(null);
  const [runEvents, setRunEvents] = useState<Record<string, EventLogEntry[]>>({});
  // local-run-id → backend-run-id so submitStepInput can POST to /resume
  // without the caller needing to know the backend id.
  const backendIdMapRef = useRef<Record<string, string>>({});
  // Polling timer — fallback when SSE dies post-submit before the run completes.
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch real persisted runs for the History tab. Falls back silently to the
  // in-session `history` (seed + live) when the backend is unreachable.
  const refreshHistory = useCallback(async () => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;
    try {
      const resp = await fetch(`${RATE_QUOTING_BASE}/runs?limit=200`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!resp.ok) return;
      const json = await resp.json();
      const rows: any[] = json?.runs ?? [];
      const mapped: RunEntry[] = rows.map((r) => {
        const rp = r.result_payload ?? {};
        const rpQuotes: QuoteResult[] = (Array.isArray(rp.quotes) ? rp.quotes : []).map((q: any) => ({
          quoteId: q.quoteId,
          carrierId: q.carrierId,
          carrierName: q.carrierName ?? q.carrierId,
          carrierLogoURL: q.carrierLogoURL,
          premiumAmount: q.premiumAmount ?? q.totalAmount,
          totalAmount: q.totalAmount ?? q.premiumAmount,
          quoteType: q.quoteType,
          statusCode: q.quoteStatus?.statusCode ?? q.quoteType,
          carrierBridgingLink: q.carrierBridgingLink,
          failureReason: q.failures?.[0]?.reason,
          contractTerm: q.contractTerm,
        }));
        return {
          id: String(r.id),
          status: (r.status as RunStatus) ?? 'completed',
          startedAt: r.started_at ? new Date(r.started_at) : new Date(),
          completedAt: r.completed_at ? new Date(r.completed_at) : undefined,
          durationMs: r.duration_ms ?? undefined,
          label: r.insured_name
            ? `${r.workflow === 'new' ? 'New' : 'Renewal'} — ${r.insured_name}`
            : (r.row_id || 'Quote run'),
          trigger: r.trigger === 'ai' ? 'ai' : 'manual',
          executedBy: r.executed_by || 'Unknown',
          triggerSource: r.trigger_source || '',
          automationType: r.trigger === 'ai' ? 'Automated' : 'Manual',
          workflow: r.workflow === 'renewal' ? 'renewal' : 'new',
          rowId: r.row_id ?? null,
          steps: [],
          quotes: rpQuotes.length ? rpQuotes : undefined,
          policyType: rp.policy_type ?? (r.workflow === 'renewal' ? 'PAP' : undefined),
          effectiveDate: rp.effective_date ?? undefined,
        };
      });
      if (mapped.length) setHistoryRuns(mapped);
    } catch {
      /* keep fallback */
    }
  }, []);

  // The provider wraps the whole app, but run history only feeds the Rate Quoting
  // pages — load it the first time one of them is visited, not on every route.
  const { pathname } = useLocation();
  const onRateQuotingPage = /^\/(rate-quoting|rate-quote-link|commercial-lines-rating)(\/|$)/.test(pathname);
  const historyRequestedRef = useRef(false);
  useEffect(() => {
    if (!onRateQuotingPage || historyRequestedRef.current) return;
    historyRequestedRef.current = true;
    refreshHistory();
  }, [onRateQuotingPage, refreshHistory]);

  // Fetch upcoming renewals for the Renewal tab. Same fail-silent pattern as
  // `refreshHistory` — if the backend isn't reachable we leave `renewals`
  // null and the page shows its empty state. The Page owns the trigger so it
  // can pass the user's date-filter picks as `start`/`end`.
  const refreshRenewals = useCallback(async (opts?: {
    daysBeforeExpiry?: number;
    start?: string;
    end?: string;
    search?: string;
    lobType?: string;
  }) => {
    const authToken = localStorage.getItem('authToken');
    const ncToken   = localStorage.getItem('amsToken') || '';
    if (!authToken) return;
    setRenewals(null);                   // flip to "loading" so the UI shows the spinner
    const params = new URLSearchParams();
    if (opts?.daysBeforeExpiry && opts.daysBeforeExpiry > 0) {
      params.set('days_before', String(opts.daysBeforeExpiry));
    }
    if (opts?.start)   params.set('start',    opts.start);
    if (opts?.end)     params.set('end',      opts.end);
    if (opts?.search)  params.set('search',   opts.search.trim());
    if (opts?.lobType) params.set('lob_type', opts.lobType);
    try {
      const url = `${PROSPECTS_BASE}/renewals${params.toString() ? `?${params}` : ''}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}`, NCToken: ncToken },
      });
      if (!resp.ok) return;
      const json = await resp.json();
      const rows: any[] = json?.renewals ?? [];
      const win = json?.window;
      if (win?.start && win?.end) setRenewalWindow({ start: win.start, end: win.end });
      setRenewals(rows.map(mapRenewalRow));
    } catch {
      setRenewals([]);
    }
  }, []);

  const refreshProspects = useCallback(async (opts?: {
    search?: string;
    page?: number;
    pageSize?: number;
    fromDate?: string;
    toDate?: string;
  }) => {
    const authToken = localStorage.getItem('authToken');
    const ncToken   = localStorage.getItem('amsToken') || '';
    if (!authToken || !ncToken) return;
    setProspects(null);
    const params = new URLSearchParams();
    if (opts?.search)   params.set('search',   opts.search);
    if (opts?.page)     params.set('page',      String(opts.page));
    if (opts?.pageSize) params.set('pageSize',  String(opts.pageSize));
    if (opts?.fromDate) params.set('fromDate',  opts.fromDate);
    if (opts?.toDate)   params.set('toDate',    opts.toDate);
    try {
      const resp = await fetch(`${PROSPECTS_BASE}/prospects?${params}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          NCToken: ncToken,
        },
      });
      if (!resp.ok) { setProspects([]); return; }
      const json = await resp.json();
      const rows: any[] = json?.prospects ?? [];
      setProspects(rows.map((r: any): QuoteRow => ({
        id:       String(r.databaseId ?? r.id ?? ''),
        date:     '—',
        insured:  r.commercialName || r.name || [r.firstName, r.lastName].filter(Boolean).join(' ') || '—',
        carrier:  '—',
        premium:  '—',
        status:   'pending',
        // OData InsuredDetailList uses "state"; GetInsuredList used "stateAbv" — handle both
        phone:    r.phone || r.cellPhone || undefined,
        notes:    [r.city, r.state || r.stateAbv].filter(Boolean).join(', ') || undefined,
        producer: r.agentsAndCSRS?.length
          ? r.agentsAndCSRS.map((a: any) => `${a.firstName} ${a.lastName}`.trim()).join(', ')
          : undefined,
        rawProspect: r,
      })));
      setProspectsTotal(json?.total ?? 0);
      setProspectsPage(json?.page ?? 1);
      setProspectsTotalPages(json?.totalPages ?? 1);
    } catch {
      setProspects([]);
    }
  }, []);

  const refreshOpportunities = useCallback(async (insuredDatabaseId: string) => {
    const authToken = localStorage.getItem('authToken');
    const ncToken   = localStorage.getItem('amsToken') || '';
    if (!authToken || !ncToken || !insuredDatabaseId) return;
    setOpportunities(null);  // signal loading
    try {
      const resp = await fetch(
        `${PROSPECTS_BASE}/insured/${encodeURIComponent(insuredDatabaseId)}/opportunities`,
        { headers: { Authorization: `Bearer ${authToken}`, NCToken: ncToken } },
      );
      if (!resp.ok) { setOpportunities([]); return; }
      const json = await resp.json();
      const rows: any[] = json?.opportunities ?? [];
      setOpportunities(rows.map((r: any): OpportunityRecord => ({
        databaseId:           r.databaseId       ?? '',
        lineOfBusinessName:   r.lineOfBusinessName ?? null,
        neededBy:             r.neededBy          ?? null,
        opportunityStageName: r.opportunityStageName ?? null,
        winProbability:       r.winProbability    ?? null,
        agencyCommission:     r.agencyCommission  ?? null,
        assignedTo:           Array.isArray(r.assignedTo) ? r.assignedTo : [],
        description:          r.description       ?? null,
        policyNumbers:        Array.isArray(r.policyNumbers) ? r.policyNumbers : [],
        createdFromRenewal:   r.createdFromRenewal ?? false,
        createDate:           r.createDate        ?? '',
        changeDate:           r.changeDate        ?? '',
        insuredDatabaseId:    r.insuredDatabaseId ?? insuredDatabaseId,
      })));
      setOpportunitiesTotal(json?.total ?? rows.length);
    } catch {
      setOpportunities([]);
    }
  }, []);

  const clearOpportunities = useCallback(() => {
    setOpportunities(null);
    setOpportunitiesTotal(0);
  }, []);

  const refreshRunEvents = useCallback(async (backendRunId: string) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken || !backendRunId) return;
    try {
      const resp = await fetch(`${RATE_QUOTING_BASE}/runs/${backendRunId}/history`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!resp.ok) return;
      const json = await resp.json();
      const events: EventLogEntry[] = Array.isArray(json?.events) ? json.events : [];
      if (events.length) {
        setRunEvents(prev => ({ ...prev, [backendRunId]: events }));
      }
    } catch {/* silent */}
  }, []);

  // ── Settings: load from API on mount, fall back to localStorage ──────────
  // Extracted into a stable callback so SettingsView can call it on open
  // (picks up isActive changes made on the MomentumRoutinesPage).
  const refreshSettings = useCallback(() => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) { setSettingsLoading(false); return; }
    setSettingsLoading(true);
    Promise.all([
      fetch(`${RATE_QUOTING_BASE}/settings`,       { headers: { Authorization: `Bearer ${authToken}` } }),
      fetch(`${RATE_QUOTING_BASE}/voice-settings`, { headers: { Authorization: `Bearer ${authToken}` } }),
    ])
      .then(async ([sResp, vResp]) => {
        const json  = sResp.ok  ? await sResp.json()  : null;
        const vjson = vResp.ok  ? await vResp.json()  : null;
        if (json) {
          const next: RateQuoteLinkSettings = {
            renewal: { ...DEFAULT_SETTINGS.renewal, ...(json.renewal ?? {}) },
            newLead: { ...DEFAULT_SETTINGS.newLead,  ...(json.newLead  ?? {}) },
            voice:   { ...DEFAULT_VOICE_SETTINGS,    ...(vjson         ?? {}) },
            _meta:   { ...(DEFAULT_SETTINGS._meta ?? {}), ...(json._meta ?? {}) },
          };
          setSettings(next);
          try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch {/* ignore */}
        } else {
          // API unavailable — fall back to localStorage so toggles always reflect
          // the last known state (including changes from MomentumRoutinesPage).
          try {
            const raw = localStorage.getItem(SETTINGS_KEY);
            if (raw) {
              const parsed = JSON.parse(raw);
              setSettings({
                renewal: { ...DEFAULT_SETTINGS.renewal, ...(parsed.renewal ?? {}) },
                newLead: { ...DEFAULT_SETTINGS.newLead,  ...(parsed.newLead  ?? {}) },
                voice:   { ...DEFAULT_VOICE_SETTINGS,    ...(parsed.voice    ?? {}) },
                _meta:   { ...(DEFAULT_SETTINGS._meta ?? {}), ...(parsed._meta ?? {}) },
              });
            }
          } catch {/* ignore */}
        }
      })
      .catch(() => {/* keep existing state */})
      .finally(() => setSettingsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Settings are loaded from localStorage on init (fast, no API call).
  // The API fetch only happens when the Settings sidebar is opened
  // (SettingsView calls refreshSettings on mount). This avoids unnecessary
  // network calls on every page load / navigation.
  useEffect(() => { setSettingsLoading(false); }, []);

  const updateSettings = useCallback((next: RateQuoteLinkSettings) => {
    setSettings(next);
    // Persist to localStorage immediately (fast)
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch {/* ignore */}
    // Persist to API (best-effort)
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    // Include browser timezone so the backend can schedule in the right zone.
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    // Strip voice — it has its own table and endpoint.
    const { voice: _voice, ...rest } = next;
    const bodyToSave = { ...rest, renewal: { ...next.renewal, timezone } };

    fetch(`${RATE_QUOTING_BASE}/settings`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyToSave),
    }).then(r => { if (!r.ok) console.error('[PLR-Settings] PUT /settings failed:', r.status); })
      .catch(e => console.error('[PLR-Settings] PUT /settings error:', e));

    // Voice settings go to their own endpoint / table.
    if (next.voice) {
      fetch(`${RATE_QUOTING_BASE}/voice-settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(next.voice),
      }).then(r => { if (!r.ok) console.error('[PLR-Settings] PUT /voice-settings failed:', r.status); })
        .catch(e => console.error('[PLR-Settings] PUT /voice-settings error:', e));
    }

    // ── Auto-upsert Momentum Routines for automation ───────────────────────
    // Routine IDs are persisted in rate_quote_settings._meta (all 3: newLead,
    // renewal, voice). On save, we always pass the existing ID so the C# upsert
    // updates the existing routine instead of creating a duplicate.
    const routineBaseUrl = `${process.env.REACT_APP_Auth_Url || 'https://momentumampapi-stage.insurancegig.com'}/api/MomentumRoutine`;
    const routineHeaders = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };

    const upsertRoutine = async (
      body: Record<string, unknown>,
      existingId: string | null | undefined,
    ): Promise<string | null> => {
      try {
        const payload = existingId ? { ...body, id: existingId } : body;
        const resp = await fetch(`${routineBaseUrl}/upsert`, {
          method: 'POST',
          headers: routineHeaders,
          body: JSON.stringify(payload),
        });
        if (!resp.ok) {
          console.error(`[PLR-Settings] Routine upsert failed (${resp.status}) for "${body.name}":`, await resp.text().catch(() => ''));
          return existingId ?? null;
        }
        const json = await resp.json();
        return json?.id ? String(json.id) : existingId ?? null;
      } catch (e) {
        console.error(`[PLR-Settings] Routine upsert error for "${body.name}":`, e);
        return existingId ?? null;
      }
    };

    (async () => {
      const meta = next._meta ?? {};

      // Only touch routines that need action:
      //   enabled + has ID   → UPDATE (isActive: true)
      //   enabled + no ID    → CREATE (isActive: true)
      //   disabled + has ID  → UPDATE (isActive: false)
      //   disabled + no ID   → SKIP (don't create disabled routines)
      const shouldUpsert = (enabled: boolean, existingId: string | null | undefined) =>
        enabled || !!existingId;

      const [newLeadId, renewalId, voiceId] = await Promise.all([
        shouldUpsert(!!next.newLead.enabled, meta.newLeadRoutineId)
          ? upsertRoutine(
              {
                name:            'Rate Quote — New Lead Automation',
                description:     'Auto-triggers rate quoting for new NowCerts prospects every 3 hours.',
                prompt:          'Fetch recent NowCerts prospects and trigger rate quoting for new leads.',
                skillIds:        ['rate-quoting-new-lead'],
                skillNames:      ['Rate Quoting — New Lead'],
                scheduleType:    'interval',
                intervalMinutes: 180,
                isActive:        !!next.newLead.enabled,
                isSystem:        true,
              },
              meta.newLeadRoutineId,
            )
          : Promise.resolve(null),
        shouldUpsert(!!next.renewal.enabled, meta.renewalRoutineId)
          ? upsertRoutine(
              {
                name:         'Rate Quote — Renewal Automation',
                description:  `Auto-triggers renewal rate quoting daily at ${next.renewal.autoRunTime}.`,
                prompt:       'Fetch policies expiring soon and trigger renewal rate quoting.',
                skillIds:     ['rate-quoting-renewal'],
                skillNames:   ['Rate Quoting — Renewal'],
                scheduleType: 'daily',
                scheduleTime: next.renewal.autoRunTime,
                isActive:     !!next.renewal.enabled,
                isSystem:     true,
              },
              meta.renewalRoutineId,
            )
          : Promise.resolve(null),
        shouldUpsert(!!next.voice?.enabled, meta.voiceRoutineId)
          ? upsertRoutine(
              {
                name:            'Rate Quote — Voice Lead Interest',
                description:     'Auto-calls new prospects every 10 hours via Momentum Voice AI to check insurance interest.',
                prompt:          'Call new NowCerts prospects, gauge interest in personal lines insurance, and auto-trigger quoting for interested leads.',
                skillIds:        ['rate-quoting-voice-calling'],
                skillNames:      ['Rate Quoting — Voice Calling'],
                scheduleType:    'interval',
                intervalMinutes: 720,
                isActive:        !!next.voice?.enabled,
                isSystem:        true,
              },
              meta.voiceRoutineId,
            )
          : Promise.resolve(null),
      ]);

      const updatedMeta = {
        newLeadRoutineId: newLeadId ?? meta.newLeadRoutineId ?? null,
        renewalRoutineId: renewalId ?? meta.renewalRoutineId ?? null,
        voiceRoutineId:   voiceId ?? meta.voiceRoutineId ?? null,
      };
      const withMeta: RateQuoteLinkSettings = { ...next, _meta: updatedMeta };
      setSettings(withMeta);
      try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(withMeta)); } catch {/* ignore */}
      // Persist routine IDs + enabled flags back to API so they survive re-login.
      fetch(`${RATE_QUOTING_BASE}/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...bodyToSave, _meta: updatedMeta }),
      }).then(r => { if (!r.ok) console.error('[PLR-Settings] PUT /settings (_meta) failed:', r.status); })
        .catch(e => console.error('[PLR-Settings] PUT /settings (_meta) error:', e));
    })();
  }, []);

  // ── Local simulation (fallback when the backend isn't reachable) ────────
  const simulateRun = useCallback((id: string, startedAt: Date, steps: WorkflowStep[]) => {
    let cursor = 0;
    const runStep = () => {
      if (cursor >= steps.length) {
        setHistory(prev => prev.map(h => {
          if (h.id !== id) return h;
          const done = new Date();
          return { ...h, status: 'completed', completedAt: done, durationMs: done.getTime() - startedAt.getTime() };
        }));
        setIsRunning(false);
        setActiveRunId(null);
        return;
      }
      const stepIdx = cursor;
      const stepStart = new Date();
      setHistory(prev => prev.map(h => {
        if (h.id !== id) return h;
        const next = h.steps.map((s, i) =>
          i === stepIdx ? { ...s, status: 'running' as StepStatus, startedAt: stepStart } : s,
        );
        return { ...h, steps: next };
      }));
      const stepDuration = 600 + Math.round(Math.random() * 800);
      window.setTimeout(() => {
        setHistory(prev => prev.map(h => {
          if (h.id !== id) return h;
          const next = h.steps.map((s, i) =>
            i === stepIdx
              ? { ...s, status: 'completed' as StepStatus, completedAt: new Date(), durationMs: stepDuration }
              : s,
          );
          return { ...h, steps: next };
        }));
        cursor++;
        runStep();
      }, stepDuration);
    };
    runStep();
  }, []);

  // ── Polling fallback — kicks in when SSE dies before the run completes ──
  // Polls GET /runs/{id} every 5 s until status is completed/failed (max 6 min).
  const startPollingRun = useCallback((backendRunId: string, localId: string) => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    let attempts = 0;
    const MAX_ATTEMPTS = 72; 

    const poll = async () => {
      if (attempts++ > MAX_ATTEMPTS) {
        setHistory(prev => prev.map(h =>
          h.id === localId && h.status === 'running'
            ? { ...h, status: 'failed' as RunStatus, completedAt: new Date() }
            : h,
        ));
        setIsRunning(false);
        setActiveRunId(null);
        return;
      }
      const authToken = localStorage.getItem('authToken') || '';
      try {
        const resp = await fetch(`${RATE_QUOTING_BASE}/runs/${backendRunId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!resp.ok) { pollingTimerRef.current = setTimeout(poll, 5000); return; }
        const snap = await resp.json();
        const br = snap?.run;
        if (!br) { pollingTimerRef.current = setTimeout(poll, 5000); return; }

        if (br.status === 'completed') {
          pollingTimerRef.current = null;
          const quotes = mapQuoteResults(br.result_payload);
          const recommendation = br.result_payload?.recommendation ?? null;
          setHistory(prev => prev.map(h => {
            if (h.id !== localId) return h;
            if (h.status === 'completed') return h; // SSE already handled it
            return {
              ...h,
              status: 'completed' as RunStatus,
              completedAt: br.completed_at ? new Date(br.completed_at) : new Date(),
              durationMs: br.duration_ms ?? h.durationMs,
              quotes,
              recommendation,
            };
          }));
          setIsRunning(false);
          setActiveRunId(null);
          refreshHistory();
        } else if (br.status === 'failed') {
          pollingTimerRef.current = null;
          setHistory(prev => prev.map(h =>
            h.id === localId && h.status !== 'completed'
              ? { ...h, status: 'failed' as RunStatus, completedAt: new Date() }
              : h,
          ));
          setIsRunning(false);
          setActiveRunId(null);
        } else {
          // Still running — keep polling
          pollingTimerRef.current = setTimeout(poll, 5000);
        }
      } catch {
        pollingTimerRef.current = setTimeout(poll, 5000);
      }
    };

    pollingTimerRef.current = setTimeout(poll, 4000); // first check after 4 s
  }, [refreshHistory]);
  const subscribeStream = useCallback((backendRunId: string, localId: string) => {
    const ncToken = localStorage.getItem('amsToken') || '';
    const streamUrl = `${RATE_QUOTING_BASE}/runs/${backendRunId}/stream${ncToken ? `?nc_token=${encodeURIComponent(ncToken)}` : ''}`;
    const es = new EventSource(streamUrl);

    es.onmessage = (ev: MessageEvent) => {
      let data: any;
      try { data = JSON.parse(ev.data); } catch { return; }

      if (data.scope === 'step' && data.stepId) {
        const stepStatus = data.status as StepStatus;
        const awaiting = data.awaitingInput as AwaitingInputPrompt | null | undefined;
        const progress = Array.isArray(data.progress)
          ? (data.progress as ProgressItem[])
          : undefined;
        setHistory(prev => prev.map(h => h.id === localId ? {
          ...h,
          status: stepStatus === 'awaiting_input' ? 'awaiting_input' : h.status,
          steps: h.steps.map(s => s.id === data.stepId
            ? {
                ...s,
                status: stepStatus,
                durationMs: data.durationMs ?? s.durationMs,
                error: data.error ?? s.error,
                progress: progress ?? s.progress,
                awaitingInput: stepStatus === 'awaiting_input' ? (awaiting ?? s.awaitingInput) : null,
              }
            : s),
        } : h));
      } else if (data.scope === 'run') {
        // SSE delivered the completion — cancel any active polling fallback
        if (pollingTimerRef.current) {
          clearTimeout(pollingTimerRef.current);
          pollingTimerRef.current = null;
        }
        const quotes = mapQuoteResults(data.result);
        const recommendation = data.result?.recommendation ?? null;
        setHistory(prev => prev.map(h => h.id === localId ? {
          ...h,
          status: (data.status as RunStatus) ?? 'completed',
          completedAt: new Date(),
          durationMs: data.durationMs ?? h.durationMs,
          quotes,
          recommendation,
        } : h));
        setIsRunning(false);
        setActiveRunId(null);
        refreshHistory();
        refreshRunEvents(backendRunId);
        // For failed runs close immediately. For completed runs keep the stream
        // open — the SSE handler generates the proposal inline and emits
        // scope:'auto_proposal_step' progress frames followed by scope:'auto_proposal'.
        if ((data.status as RunStatus) !== 'completed') {
          es.close();
        }
      } else if (data.scope === 'auto_proposal_step') {
        // Live step progress from inline proposal generation — update card label.
        setHistory(prev => prev.map(h => h.id === localId ? {
          ...h,
          autoProposalStep: { step: data.step, label: data.label },
        } : h));
      } else if (data.scope === 'auto_proposal') {
        // Inline proposal generation completed — update state and close stream.
        const ap = data.auto_proposal ?? null;
        setHistory(prev => prev.map(h => h.id === localId ? {
          ...h,
          autoProposal: ap,
        } : h));
        es.close();
        refreshHistory();
      } else if (data.scope === 'quote_summary') {
        // Quote Summary PDF generated — update run entry with file URL.
        setHistory(prev => prev.map(h => h.id === localId ? {
          ...h,
          quoteSummary: data.quote_summary ?? null,
        } : h));
      } else if (data.scope === 'event') {
        // Append an event log entry to the run's event list (no de-dup needed —
        // SSE delivers each event exactly once; duplicates are guarded by id check).
        const entry = data as EventLogEntry;
        if (entry.id != null) {
          setRunEvents(prev => {
            const existing = prev[backendRunId] ?? [];
            if (existing.some(e => e.id === entry.id)) return prev;
            return { ...prev, [backendRunId]: [...existing, entry] };
          });
        }
      }
    };

    es.onerror = () => {
      es.close();
      // If the run was actively running (post-submit), start the polling fallback
      // instead of immediately marking as failed — the workflow may still be running.
      setHistory(prev => {
        const entry = prev.find(h => h.id === localId);
        if (entry?.status === 'running') {
          setTimeout(() => startPollingRun(backendRunId, localId), 0);
          return prev; // polling will update status when done
        }
        // Not running (e.g. died during awaiting_input wait) — no change needed
        return prev;
      });
      setIsRunning(false);
      setActiveRunId(null);
    };
  }, [refreshHistory, startPollingRun]);

  const triggerRun = useCallback((opts: {
    rowId: string;
    workflow: 'new' | 'renewal';
    label: string;
    insuredName?: string;
    insuredId?: string;
    withReview?: boolean;
    policyType?: 'PAP' | 'HO' | 'BUNDLE';
    effectiveDate?: string;
  }) => {
    const id = `run-${Date.now()}`;
    const startedAt = new Date();
    const steps = buildSteps(opts.workflow, opts.policyType);

    const newRun: RunEntry = {
      id, status: 'running', startedAt,
      label: opts.label,
      trigger: 'manual', executedBy: localStorage.getItem('userName') || 'Unknown',
      triggerSource: 'Manual click (table row)', automationType: 'Manual',
      workflow: opts.workflow, rowId: opts.rowId,
      steps,
    };

    setIsRunning(true);
    setActiveRunId(id);
    setHistory(prev => [newRun, ...prev]);

    // Try the live backend; fall back to local simulation on any failure.
    const authToken = localStorage.getItem('authToken') || '';
    // RateMCP is a rating system — it needs the rater credential (rateToken),
    // which it exchanges for a NowCerts access token. The amsToken (NowCerts
    // access token) is NOT a valid API key for that exchange. Fall back to
    // amsToken only if rateToken is absent.
    const rateToken = localStorage.getItem('rateToken') || '';
    const ncToken = rateToken || localStorage.getItem('amsToken') || '';
    const insuredName = opts.insuredName || opts.label.split('—').pop()?.trim() || opts.label;

    if (!authToken || !ncToken) {
      simulateRun(id, startedAt, steps);
      return id;
    }

    (async () => {
      try {
        const resp = await fetch(`${RATE_QUOTING_BASE}/runs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            row_id:       opts.rowId,
            insured_name: insuredName,
            insured_id:   opts.insuredId || "",
            workflow:     opts.workflow,
            nc_token:     ncToken,
            with_review:  !!opts.withReview,
            policy_type:  opts.policyType || 'PAP',
            ...(opts.effectiveDate ? { effective_date: opts.effectiveDate } : {}),
          }),
        });
        if (!resp.ok) throw new Error(`start run failed: ${resp.status}`);
        const json = await resp.json();
        if (!json?.runId) throw new Error('no runId returned');
        backendIdMapRef.current[id] = json.runId;
        subscribeStream(json.runId, id);
      } catch {
        // Backend not available / errored before stream — simulate instead.
        simulateRun(id, startedAt, steps);
      }
    })();
    return id;
  }, [isRunning, simulateRun, subscribeStream]);

  // POST user-provided inputs to /resume, restore running state (SSE likely
  // died during the review wait), re-subscribe the SSE stream, and start the
  // polling fallback so quotes appear automatically without a page refresh.
  const submitStepInput = useCallback(async (
    runId: string,
    stepId: string,
    inputs: Record<string, any>,
  ) => {
    const backendId = backendIdMapRef.current[runId] || runId;
    const authToken = localStorage.getItem('authToken') || '';

    // Optimistically flip step + run back to running
    setHistory(prev => prev.map(h => h.id === runId ? {
      ...h,
      status: 'running' as RunStatus,
      steps: h.steps.map(s => s.id === stepId
        ? { ...s, status: 'running' as StepStatus, awaitingInput: null }
        : s),
    } : h));
    // Restore isRunning + activeRunId — SSE cleared them when the stream died
    setIsRunning(true);
    setActiveRunId(runId);

    try {
      const resp = await fetch(`${RATE_QUOTING_BASE}/runs/${backendId}/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ stepId, inputs }),
      });
      if (resp.ok) {
        // Re-open SSE — stream died during the review wait, workflow is now running again
        subscribeStream(backendId, runId);
      }
    } catch {
      /* fetch failed — fall through to polling */
    }
    // Always start polling as a safety net: if SSE misses the completion event
    // (fast run, reconnect lag), polling guarantees quotes show up.
    startPollingRun(backendId, runId);
  }, [subscribeStream, startPollingRun]);

  // Re-attach to an existing backend run (e.g. clicked "Submit & Rate" from history).
  // Fetches the snapshot, builds a RunEntry, seeds it into history, and starts SSE.
  const reattachRun = useCallback(async (backendRunId: string) => {
    const authToken = localStorage.getItem('authToken') || '';
    if (!authToken) return;

    // Always fetch a fresh snapshot so we know the current status.
    // This is the single source of truth for deciding whether to open SSE.
    try {
      const resp = await fetch(`${RATE_QUOTING_BASE}/runs/${backendRunId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!resp.ok) return;
      const snap = await resp.json();
      if (!snap?.run) return;

      const br = snap.run;
      const steps: WorkflowStep[] = (snap.steps ?? []).map((s: any) => ({
        id:            s.step_id,
        label:         s.step_label,
        status:        s.status as StepStatus,
        durationMs:    s.duration_ms ?? undefined,
        error:         s.error_message ?? null,
        progress:      Array.isArray(s.progress) ? s.progress : [],
        awaitingInput: s.awaiting_input ?? null,
      }));

      // Reuse the existing local ID if this run is already tracked in session;
      // otherwise create a stable reattach ID so we don't duplicate history entries.
      const existingLocalId = Object.entries(backendIdMapRef.current)
        .find(([, bid]) => bid === backendRunId)?.[0];
      const localId = existingLocalId ?? `run-reattach-${backendRunId}`;

      const runEntry: RunEntry = {
        id:             localId,
        status:         (br.status as RunStatus) ?? 'running',
        startedAt:      br.started_at ? new Date(br.started_at) : new Date(),
        label:          br.insured_name
                          ? `${br.workflow === 'new' ? 'New' : 'Renewal'} — ${br.insured_name}`
                          : 'Quote run',
        trigger:        'manual',
        executedBy:     br.executed_by || 'Unknown',
        triggerSource:  'Manual click (table row)',
        automationType: 'Manual',
        workflow:       (br.workflow as 'new' | 'renewal') ?? undefined,
        rowId:          br.row_id ?? undefined,
        steps,
        quotes:         mapQuoteResults(br.result_payload),
        recommendation: br.result_payload?.recommendation ?? null,
        autoProposal:   br.result_payload?.auto_proposal ?? null,
        currentPremium: br.result_payload?.current_premium ?? null,
        quoteSummary:   br.result_payload?.quote_summary ?? null,
      };

      backendIdMapRef.current[localId] = backendRunId;
      // Upsert: replace existing entry if present (fresher data), otherwise prepend.
      setHistory(prev =>
        prev.some(h => h.id === localId)
          ? prev.map(h => h.id === localId ? { ...h, ...runEntry } : h)
          : [runEntry, ...prev]
      );
      setActiveRunId(localId);

      if (br.status === 'completed' || br.status === 'failed') {
        // Terminal run — all data is in the snapshot.
        // NEVER open SSE: it re-triggers proposal generation even when
        // auto_proposal already exists in result_payload.
        setIsRunning(false);
        refreshRunEvents(backendRunId);
      } else {
        // Still running or paused — subscribe SSE for live step updates.
        setIsRunning(true);
        subscribeStream(backendRunId, localId);
      }
    } catch {
      /* silent */
    }
  }, [subscribeStream, refreshRunEvents]);

  const getBackendId = useCallback(
    (localId: string) => backendIdMapRef.current[localId] || localId,
    [],
  );

  const value = useMemo<RateQuoteLinkContextValue>(() => {
    const activeRun = activeRunId ? history.find(h => h.id === activeRunId) : undefined;
    const lastRun   = history.find(h => h.status !== 'running');
    return {
      history,
      isRunning,
      activeRun,
      lastRun,
      settings,
      settingsLoading,
      updateSettings,
      refreshSettings,
      triggerRun,
      submitStepInput,
      reattachRun,
      getBackendId,
      panelView,
      setPanelView,
      selectedRow,
      setSelectedRow,
      selectedQuoteRun,
      setSelectedQuoteRun,
      openSignal,
      requestOpen,
      closeSignal,
      requestClose,
      historyRuns,
      renewals,
      renewalWindow,
      refreshRenewals,
      prospects,
      prospectsTotal,
      prospectsPage,
      prospectsTotalPages,
      refreshProspects,
      opportunities,
      opportunitiesTotal,
      refreshOpportunities,
      clearOpportunities,
      runEvents,
      refreshRunEvents,
    };
  }, [history, isRunning, activeRunId, settings, settingsLoading, updateSettings, refreshSettings, triggerRun, submitStepInput, reattachRun, getBackendId, panelView, selectedRow, selectedQuoteRun, openSignal, requestOpen, closeSignal, requestClose, historyRuns, renewals, renewalWindow, refreshRenewals, prospects, prospectsTotal, prospectsPage, prospectsTotalPages, refreshProspects, opportunities, opportunitiesTotal, refreshOpportunities, clearOpportunities, runEvents, refreshRunEvents]);

  return (
    <RateQuoteLinkContext.Provider value={value}>
      {children}
    </RateQuoteLinkContext.Provider>
  );
};

export const useRateQuoteLink = (): RateQuoteLinkContextValue => {
  const ctx = useContext(RateQuoteLinkContext);
  if (!ctx) {
    return {
      history: [],
      isRunning: false,
      activeRun: undefined,
      lastRun: undefined,
      settings: DEFAULT_SETTINGS,
      settingsLoading: false,
      updateSettings: () => {},
      refreshSettings: () => {},
      triggerRun: () => '',
      submitStepInput: async () => {},
      reattachRun: async () => {},
      getBackendId: (id: string) => id,
      panelView: 'workflow',
      setPanelView: () => {},
      selectedRow: null,
      setSelectedRow: () => {},
      selectedQuoteRun: null,
      setSelectedQuoteRun: () => {},
      openSignal: 0,
      requestOpen: () => {},
      closeSignal: 0,
      requestClose: () => {},
      historyRuns: null,
      renewals: null,
      renewalWindow: null,
      refreshRenewals: async () => {},
      prospects: null,
      prospectsTotal: 0,
      prospectsPage: 1,
      prospectsTotalPages: 1,
      refreshProspects: async () => {},
      opportunities: null,
      opportunitiesTotal: 0,
      refreshOpportunities: async () => {},
      clearOpportunities: () => {},
      runEvents: {},
      refreshRunEvents: async () => {},
    };
  }
  return ctx;
};

